import React from 'react';

/**
 * Extract YouTube video ID from a URL object
 * Supports: watch URLs, shorts, youtu.be, embed, mobile URLs, etc.
 * @param {URL} urlObj - Parsed URL object
 * @returns {string|null} - Video ID or null
 */
const getYouTubeVideoId = urlObj => {
  const hostname = urlObj.hostname.replace(/^(www|m)\./, '');

  // Shortened youtu.be URLs
  if (hostname === 'youtu.be') {
    const videoId = urlObj.pathname.slice(1).split('/')[0];
    return videoId.length === 11 ? videoId : null;
  }

  // youtube.com URLs
  if (hostname === 'youtube.com') {
    // /watch?v=VIDEO_ID
    const vParam = urlObj.searchParams.get('v');
    if (vParam && vParam.length === 11) {
      return vParam;
    }

    // /shorts/VIDEO_ID, /embed/VIDEO_ID, /v/VIDEO_ID
    const pathMatch = urlObj.pathname.match(/^\/(shorts|embed|v)\/([a-zA-Z0-9_-]{11})/);
    if (pathMatch && pathMatch[2]) {
      return pathMatch[2];
    }
  }

  return null;
};

/**
 * Extract Vimeo video ID and privacy token from a URL object
 * Supports: public videos, unlisted, private (with h token), staff/showcase URLs, player.vimeo.com
 * @param {URL} urlObj - Parsed URL object
 * @returns {object|null} - Object with videoId and optional token, or null
 */
const getVimeoVideoData = urlObj => {
  const hostname = urlObj.hostname.replace(/^www\./, '');
  const pathParts = urlObj.pathname.split('/').filter(p => p);

  if (pathParts.length === 0) return null;

  // Handle player.vimeo.com/video/123456
  if (hostname === 'player.vimeo.com') {
    if (pathParts[0] === 'video' && pathParts[1] && /^\d+$/.test(pathParts[1])) {
      const token = urlObj.searchParams.get('h');
      return { videoId: pathParts[1], token };
    }
    return null;
  }

  // Standard vimeo.com URLs
  if (hostname !== 'vimeo.com') return null;

  // Look for numeric video ID anywhere in the path
  // This handles: /123456, /video/123456, /staff/123456, /channels/name/123456, etc.
  let videoId = null;
  let videoIdIndex = -1;

  for (let i = 0; i < pathParts.length; i++) {
    if (/^\d+$/.test(pathParts[i])) {
      videoId = pathParts[i];
      videoIdIndex = i;
      break;
    }
  }

  if (!videoId) return null;

  // Check for privacy token after video ID in path or in query params
  const token = (videoIdIndex >= 0 && pathParts[videoIdIndex + 1]) || urlObj.searchParams.get('h');

  return { videoId, token };
};

/**
 * Extract TikTok video ID from a URL object
 * Supports: user video URLs, mobile web URLs, embed URLs
 * Note: vm.tiktok.com short links require server-side redirect following (not supported client-side)
 * @param {URL} urlObj - Parsed URL object
 * @returns {string|null} - Video ID or null
 */
const getTikTokVideoId = urlObj => {
  const hostname = urlObj.hostname.replace(/^www\./, '');

  // User video: /@username/video/1234567890
  const userVideoMatch = urlObj.pathname.match(/\/@[\w.-]+\/video\/(\d+)/);
  if (userVideoMatch && userVideoMatch[1]) {
    return userVideoMatch[1];
  }

  // Embed URL: /embed/v3/1234567890
  const embedMatch = urlObj.pathname.match(/\/embed\/v\d+\/(\d+)/);
  if (embedMatch && embedMatch[1]) {
    return embedMatch[1];
  }

  // Mobile web URL: m.tiktok.com/v/1234567890.html or m.tiktok.com/v/1234567890
  if (hostname === 'm.tiktok.com') {
    const mobileMatch = urlObj.pathname.match(/\/v\/(\d+)/);
    if (mobileMatch && mobileMatch[1]) {
      return mobileMatch[1];
    }
  }

  // Standard shortened URL: /v/1234567890 (not vm.tiktok.com which needs API lookup)
  if (hostname === 'tiktok.com') {
    const shortMatch = urlObj.pathname.match(/\/v\/(\d+)/);
    if (shortMatch && shortMatch[1]) {
      return shortMatch[1];
    }
  }

  return null;
};

/**
 * Extract Instagram Reel ID from a URL object
 * Supports: /reel/ID, /p/ID, and /tv/ID paths
 * @param {URL} urlObj - Parsed URL object
 * @returns {string|null} - Reel ID or null
 */
const getInstagramReelId = urlObj => {
  // /reel/ABC123 or /p/ABC123 or /tv/ABC123
  const match = urlObj.pathname.match(/^\/(reel|p|tv)\/([a-zA-Z0-9_-]+)/);
  if (match && match[2]) {
    return match[2];
  }
  return null;
};

/**
 * Analyze a video URL to detect service, format validity, and ID validity
 * @param {string} url - The video URL to analyze
 * @returns {object} - Analysis result with detectedService, isValidFormat, hasValidId, embedElement
 */
export const analyzeVideoUrl = url => {
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return {
      detectedService: null,
      isValidFormat: false,
      hasValidId: false,
      embedElement: null,
    };
  }

  let detectedService = null;
  let isValidFormat = false;
  let hasValidId = false;
  let embedElement = null;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace(/^(www|m)\./, '');

    // Detect service from hostname
    if (hostname.includes('youtube') || hostname.includes('youtu.be')) {
      detectedService = 'youtube';
      // Check format validity
      if (hostname === 'youtube.com' || hostname === 'youtu.be') {
        // Valid YouTube domain structure
        if (
          urlObj.searchParams.has('v') ||
          /^\/(shorts|embed|v)\//.test(urlObj.pathname) ||
          (hostname === 'youtu.be' && urlObj.pathname.length > 1)
        ) {
          isValidFormat = true;
          // Try to get embed element to check ID validity
          embedElement = getEmbedElement(url);
          hasValidId = embedElement !== null;
        }
      }
    } else if (hostname.includes('vimeo')) {
      detectedService = 'vimeo';
      // Check format validity
      if (hostname === 'vimeo.com' || hostname === 'player.vimeo.com') {
        // Valid Vimeo domain
        if (urlObj.pathname.length > 1) {
          isValidFormat = true;
          embedElement = getEmbedElement(url);
          hasValidId = embedElement !== null;
        }
      }
    } else if (hostname.includes('tiktok')) {
      detectedService = 'tiktok';
      // Check format validity
      if (
        /\/@[\w.-]+\/video\//.test(urlObj.pathname) ||
        /\/embed\/v\d+\//.test(urlObj.pathname) ||
        /\/v\//.test(urlObj.pathname)
      ) {
        isValidFormat = true;
        embedElement = getEmbedElement(url);
        hasValidId = embedElement !== null;
      }
    } else if (hostname.includes('instagram')) {
      detectedService = 'instagram';
      // Check format validity
      if (/^\/(reel|p|tv)\//.test(urlObj.pathname)) {
        isValidFormat = true;
        embedElement = getEmbedElement(url);
        hasValidId = embedElement !== null;
      }
    }
  } catch (e) {
    // Invalid URL format - still try to detect service from string
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('youtube') || lowerUrl.includes('youtu.be')) {
      detectedService = 'youtube';
    } else if (lowerUrl.includes('vimeo')) {
      detectedService = 'vimeo';
    } else if (lowerUrl.includes('tiktok')) {
      detectedService = 'tiktok';
    } else if (lowerUrl.includes('instagram')) {
      detectedService = 'instagram';
    }
  }

  return {
    detectedService,
    isValidFormat,
    hasValidId,
    embedElement,
  };
};

/**
 * Extract video ID from URL and return an iframe embed element for supported video services.
 * Supports: YouTube (all formats including Shorts), Vimeo (public/private), TikTok, Instagram Reels
 *
 * @param {string} url - The video URL to embed
 * @returns {JSX.Element|null} - The iframe element or null if unsupported
 */
export const getEmbedElement = url => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace(/^(www|m)\./, '');

    // YouTube
    if (hostname === 'youtube.com' || hostname === 'youtu.be') {
      const videoId = getYouTubeVideoId(urlObj);
      if (videoId) {
        return (
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            width="1156"
            height="650"
            frameBorder="0"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        );
      }
    }

    // Vimeo
    if (hostname === 'vimeo.com' || hostname === 'player.vimeo.com') {
      const vimeoData = getVimeoVideoData(urlObj);
      if (vimeoData) {
        const embedUrl = vimeoData.token
          ? `https://player.vimeo.com/video/${vimeoData.videoId}?h=${vimeoData.token}`
          : `https://player.vimeo.com/video/${vimeoData.videoId}`;

        return (
          <iframe
            src={embedUrl}
            title="Vimeo video"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            width="1156"
            height="650"
            frameBorder="0"
          />
        );
      }
    }

    // TikTok
    if (hostname.includes('tiktok.com')) {
      const videoId = getTikTokVideoId(urlObj);
      if (videoId) {
        return (
          <iframe
            src={`https://www.tiktok.com/embed/v3/${videoId}`}
            title="TikTok video"
            allow="autoplay; encrypted-media"
            allowFullScreen
            width="325"
            height="575"
            frameBorder="0"
            sandbox="allow-scripts allow-same-origin"
          />
        );
      }
    }

    // Instagram
    if (hostname === 'instagram.com') {
      const reelId = getInstagramReelId(urlObj);
      if (reelId) {
        return (
          <iframe
            className="instagram-media"
            src={`https://www.instagram.com/reel/${reelId}/embed`}
            title="Instagram Reel"
            allow="autoplay; encrypted-media"
            allowFullScreen
            width="350"
            height="700"
            frameBorder="0"
            style={{
              display: 'block',
              margin: '0 auto',
              borderRadius: 16,
              maxWidth: '100%',
            }}
          />
        );
      }
    }
  } catch (e) {
    // Invalid URL format
    return null;
  }

  return null;
};
